import type { JSX } from 'react';

import { NotFound } from 'common/components/NotFound';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { AclAccessesTab } from 'features/aclAccesses/AclAccessesTab';
import { AclResourcesTab } from 'features/aclResources/AclResourcesTab';
import { Acls } from 'features/acls/Acls';
import { Admin } from 'features/admin/Admin';
import { AccountLinkingDashboard } from 'features/admin/subpages/accountLinkingDashboard/AccountLinkingDashboard';
import { ApiKeysTab } from 'features/apiKeys/ApiKeysTab';
import { ApplicationSuiteDetails } from 'features/applicationSuiteDetails/ApplicationSuiteDetails';
import { ApplicationSuites } from 'features/applicationSuites/ApplicationSuites';
import { AuthorizedIssuersTab } from 'features/authorizedIssuers/AuthorizedIssuersTab';
import { ClaimDetails } from 'features/claimDetails/ClaimDetails';
import { ClaimRoleMembershipTab } from 'features/claimRoleMembership/ClaimRoleMembershipTab';
import { ClaimsTab } from 'features/claims/ClaimsTab';
import { ClaimUserMembershipTab } from 'features/claimUserMembership/ClaimUserMembershipTab';
import { GlobalRoles } from 'features/globalRoles';
import { Help } from 'features/Help';
import { Imports } from 'features/imports/Imports';
import { LicenseAclEntriesTab } from 'features/licenseAclEntries/LicenseAclEntriesTab';
import { LicenseDetails } from 'features/licenseDetails/LicenseDetails';
import { LicensesTab } from 'features/licenses/LicensesTab';
import { LicenseUserGroupsTab } from 'features/licenseUserGroups/LicenseUserGroupsTab';
import { LicenseUsersTab } from 'features/licenseUsers/LicenseUsersTab';
import { Monitoring } from 'features/monitoring';
import { Reports } from 'features/reports';
import { AclResourceUserListReport } from 'features/reports/components/aclResourceUserListReport/aclResourceUserListReport';
import { LicenseReport } from 'features/reports/components/licenseProofOfDeliveryReport/LicenseReport';
import { UserGroupAclsReport } from 'features/reports/components/userGroupAclsReport/userGroupAclsReport';
import { UserGroupsReport } from 'features/reports/components/userGroupsProofOfDeliveryReport/UserGroupsReport';
import { UsersWithNewAccessReport } from 'features/reports/components/usersWithNewAccessReport/usersWithNewAccessReport';
import { RoleClaimMembershipTab } from 'features/roleClaimMembership/RoleClaimMembershipTab';
import { RoleDetails } from 'features/roleDetails/RoleDetails';
import { RolesTab } from 'features/roles/RolesTab';
import { RoleUserGroupTab } from 'features/roleUserGroup/RoleUserGroupTab';
import { RoleUserMembershipTab } from 'features/roleUserMembership/RoleUserMembershipTab';
import { TenantDetails } from 'features/tenantDetails/TenantDetails';
import { TenantsTab } from 'features/tenants/TenantsTab';
import { TenantUserMembershipTab } from 'features/tenantUserMembership/tenantUserMembershipTab';
import { TenantUserMembershipDetails } from 'features/tenantUserMembershipDetails/TenantUserMembershipDetails';
import { UserAclEntriesTab } from 'features/userAclEntries/UserAclEntriesTab';
import { UserClaimMembershipTab } from 'features/userClaimMembership/UserClaimMembershipTab';
import { UserProfileDetails } from 'features/userDetails/UserDetails';
import { UserGroupAclEntriesTab } from 'features/userGroupAclEntries/UserGroupAclEntriesTab';
import { UserGroupDetails } from 'features/userGroupDetails/UserGroupDetails';
import { UserGroupRoleMembershipTab } from 'features/userGroupRoleMembership/UserGroupRoleMembershipTab';
import { UserGroupsUsersTab } from 'features/userGroupsUsers/UserGroupsUsersTab';
import { UserGroupUserMembershipTab } from 'features/userGroupUserMembership/UserGroupUserMembershipTab';
import { UserRoleMembershipsTab } from 'features/userRoleMemberships/UserRoleMembershipsTab';
import { Users } from 'features/users/Users';

import { UserGroupsTab } from '../../features/userGroups/UserGroupsTab';

export type RouteDefinition = {
    path?: string;
    element: JSX.Element;
    roles?: Roles[];
    children?: RouteDefinition[];
};

export const routes: RouteDefinition[] = [
    {
        path: Routes.Tenant,
        element: <TenantDetails />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
        children: [
            {
                path: Routes.Users,
                element: <TenantUserMembershipTab />
            },
            {
                path: Routes.Licenses,
                element: <LicensesTab />
            },
            {
                path: Routes.UserGroups,
                element: <UserGroupsTab />
            },
            {
                path: Routes.Roles,
                element: <RolesTab />,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ]
            },
            {
                path: Routes.Claims,
                element: <ClaimsTab />
            },
            {
                path: Routes.Acls,
                element: <Acls />,
                children: [
                    {
                        path: Routes.AclResources,
                        element: <AclResourcesTab />
                    },
                    {
                        path: Routes.AclAccesses,
                        element: <AclAccessesTab />,
                        roles: [
                            Roles.Admin,
                            Roles.AppManager
                        ]
                    }
                ]
            },
            {
                path: Routes.AuthorizedIssuers,
                element: <AuthorizedIssuersTab />
            }
        ]
    },
    {
        path: Routes.ApplicationSuite,
        element: <ApplicationSuiteDetails />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
        children: [
            {
                path: Routes.Tenants,
                element: <TenantsTab />,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.ApiKeys,
                element: <ApiKeysTab />,
                roles: [
                    Roles.Admin,
                    Roles.AppManager
                ]
            }
        ]
    },
    {
        path: Routes.Role,
        element: <RoleDetails />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
        children: [
            {
                path: Routes.Users,
                element: <RoleUserMembershipTab />,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.UserGroups,
                element: <UserGroupRoleMembershipTab />,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.Claims,
                element: <RoleClaimMembershipTab />,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            }
        ]
    },
    {
        path: Routes.Claim,
        element: <ClaimDetails />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
        children: [
            {
                path: Routes.Users,
                element: <ClaimUserMembershipTab />,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.Roles,
                element: <ClaimRoleMembershipTab/>,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            }
        ]
    },
    {
        path: Routes.UserGroup,
        element: <UserGroupDetails />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
        children: [
            {
                path: Routes.Users,
                element: <UserGroupsUsersTab />,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.Roles,
                element: <RoleUserGroupTab />,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.AclEntries,
                element: <UserGroupAclEntriesTab />
            }
        ]
    },
    {
        path: Routes.User,
        element: <TenantUserMembershipDetails />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
            Roles.GlobalRoleManager
        ],
        children: [
            {
                path: Routes.Roles,
                element: <UserRoleMembershipsTab />,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager
                ]
            },
            {
                path: Routes.UserGroups,
                element: <UserGroupUserMembershipTab/>,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.Claims,
                element: <UserClaimMembershipTab/>,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.AclEntries,
                element: <UserAclEntriesTab />,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            }
        ]
    },
    {
        path: Routes.License,
        element: <LicenseDetails />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
        children: [
            {
                path: Routes.Users,
                element: <LicenseUsersTab />
            },
            {
                path: Routes.UserGroups,
                element: <LicenseUserGroupsTab />
            },
            {
                path: Routes.AclEntries,
                element: <LicenseAclEntriesTab />,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            }
        ]
    },
    {
        path: Routes.UserGroupsReport,
        element: <UserGroupsReport />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.UserGroupACLsReport,
        element: <UserGroupAclsReport />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.LicensesReport,
        element: <LicenseReport />,
        roles: [
            Roles.Admin,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.UsersWithNewAccessReport,
        element: <UsersWithNewAccessReport />,
        roles: [
            Roles.Admin,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.AclResourceUserListReport,
        element: <AclResourceUserListReport />,
        roles: [
            Roles.Admin,
            Roles.UserManager,
            Roles.AppManager,
        ],
    },
    {
        path: Routes.ApplicationSuites,
        element: <ApplicationSuites />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager
        ],
    },
    {
        path: Routes.Users,
        element: <Users />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.UsersDetails,
        element: <UserProfileDetails />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
            Roles.GlobalRoleManager
        ],
    },
    {
        path: Routes.Reports,
        element: <Reports />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.Monitoring,
        element: <Monitoring />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.Admin,
        element: <Admin />,
        roles: [
            Roles.Admin,
            Roles.MultiTenantRO,
            Roles.UserManager
        ],
    },
    {
        path: Routes.AccountLinkingDashboard,
        element: <AccountLinkingDashboard />,
        roles: [
            Roles.Admin,
            Roles.MultiTenantRO,
            Roles.UserManager
        ]
    },
    {
        path: Routes.Imports,
        element: <Imports />,
        roles: [
            Roles.Admin,
            Roles.UserManager
        ]
    },
    {
        path: Routes.GlobalRoles,
        element: <GlobalRoles />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.Help,
        element: <Help />,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.Home,
        element: <></>,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: '*',
        element: <NotFound />,
    }
];
